import { useState, useEffect } from 'react'
import type { SeverityLevel } from '../types'
import type { CogoSurfaceProposal } from '@/features/chat/services/studio-agent-runtime-a2a'
import type { GeneratedApplicationProposal } from '@cogo/a2a-gateway/generated-application-proposal'
import type {
  ArtifactRevisionEnvelope,
  ExistingProjectGeneratedArtifactCandidate,
} from '@/features/generated-artifact'

// ─── Types ───────────────────────────────────────────────────

export interface IntentPreviewData {
  id: string
  action_verb: string
  severity: SeverityLevel
  diff_rows: Array<{ field: string; before: string; after: string }>
  impact_summary: string
  resolved: boolean
  /**
   * A surface to preview, of either declared kind.
   *
   * `CogoSurfaceProposal` is the ephemeral A2A/QuickJS result — previewable,
   * no persistence authority. `GeneratedApplicationProposal` is a Cogo
   * application the PLATFORM generated with this user's own credential; it
   * carries the same approval contract from the same stamp site and pins
   * `interactive: false`.
   *
   * ONE field, so the host below keeps ONE render path. Two fields would mean
   * two ways to draw a Cogo application in the same card.
   */
  surface_proposal?: CogoSurfaceProposal | GeneratedApplicationProposal
  /**
   * Exact authenticated Chat proposal lineage. A generated surface without
   * this candidate remains preview-only and must fail closed on approval.
   */
  generated_artifact_candidate?: ExistingProjectGeneratedArtifactCandidate
  anchor?: {
    anchor_id: string
    label: string
    kind: string
    target_formats: string[]
    evidence_refs: string[]
  }
  requirement?: {
    requirement_id: string
    intent: string
    body: string
    acceptance_criteria: string[]
    proposal?: {
      body: string
      source: "qwen-gateway" | "local-fallback"
      mode: "text-replacement" | "requirement"
    }
  }
  semantic_patch?: {
    patch_id: string
    operation: string
    target: string
    target_kind: string
    authority: "semantic-patch-draft"
    risk: SeverityLevel
    validation_plan: string[]
    evidence_refs: string[]
    may_patch_cogo_json_directly: false
  }
}

// ─── Module-level event bus (mirrors onBlockDiff pattern) ────

let _preview: IntentPreviewData | null = null
const _listeners = new Set<(data: IntentPreviewData | null) => void>()

function notifyListeners() {
  for (const l of _listeners) l(_preview)
}

/**
 * Subscribe to intent preview changes.
 * Returns unsubscribe function.
 */
export function onIntentPreview(
  listener: (data: IntentPreviewData | null) => void,
): () => void {
  _listeners.add(listener)
  return () => _listeners.delete(listener)
}

/**
 * Emit an intent preview event.
 * Called by Agent runtime when proposing actions.
 */
export function emitIntentPreview(data: IntentPreviewData | null): void {
  _preview = data
  notifyListeners()
}

/**
 * Resolve the current intent preview (approve or dismiss).
 * Sets resolved=true and notifies listeners.
 */
export function isIntentPreviewCurrent(id: string): boolean {
  return _preview?.id === id && !_preview.resolved
}

function generatedArtifactPreviewIdentity(
  preview: IntentPreviewData | null,
): string | null {
  const candidate = preview?.generated_artifact_candidate
  const envelope: ArtifactRevisionEnvelope | undefined = candidate?.envelope
  if (!candidate || !envelope) return null
  const target =
    envelope.target.kind === 'existing-project'
      ? [envelope.target.kind, envelope.target.projectId]
      : envelope.target.kind === 'existing-bot-project'
        ? [
            envelope.target.kind,
            envelope.target.projectId,
            envelope.target.ownerBotProfileId,
          ]
        : [
            envelope.target.kind,
            envelope.target.ownerBotProfileId,
            envelope.target.suggestedName,
          ]
  const evidenceRefs = envelope.evidenceRefs
    .map((evidence) => [evidence.ref, evidence.contentHash] as const)
    .sort(([leftRef, leftHash], [rightRef, rightHash]) => {
      if (leftRef !== rightRef) return leftRef < rightRef ? -1 : 1
      if (leftHash === rightHash) return 0
      return leftHash < rightHash ? -1 : 1
    })
  return JSON.stringify([
    envelope.schemaVersion,
    envelope.artifactId,
    target,
    envelope.revision.authority,
    envelope.revision.value,
    envelope.parentRevision?.authority ?? null,
    envelope.parentRevision?.value ?? null,
    envelope.contentHash,
    envelope.requestId,
    envelope.runId,
    evidenceRefs,
    envelope.persistenceReceipt,
    candidate.requestedByUserId,
  ])
}

export function bindGeneratedIntentPreviewResolution(
  expected: IntentPreviewData,
) {
  const expectedId = expected.id
  const expectedIdentity = generatedArtifactPreviewIdentity(expected)
  if (!expectedIdentity) return null

  const isCurrent = () =>
    Boolean(
      _preview &&
        !_preview.resolved &&
        _preview.id === expectedId &&
        generatedArtifactPreviewIdentity(_preview) === expectedIdentity,
    )

  return Object.freeze({
    isCurrent,
    resolve(): boolean {
      if (!isCurrent()) return false
      _preview = { ..._preview!, resolved: true }
      notifyListeners()
      return true
    },
  })
}

export function resolveIntentPreview(expectedId?: string): boolean {
  if (!_preview || (expectedId !== undefined && _preview.id !== expectedId)) {
    return false
  }
  _preview = { ..._preview, resolved: true }
  notifyListeners()
  return true
}

// ─── Hook ────────────────────────────────────────────────────

export function useIntentPreview(): IntentPreviewData | null {
  const [preview, setPreview] = useState<IntentPreviewData | null>(_preview)

  useEffect(() => {
    return onIntentPreview(setPreview)
  }, [])

  return preview
}
